import * as React from 'react';

import {
  cn,
  Col,
  MaterialSymbol,
  Row,
  Tab,
  ThemeColor,
  TouchButton,
} from '@rematter/ui-web';
import { LucideTrash2 } from 'lucide-react';

export type TouchDialogHeaderTab = {
  key: string;
  label: string;
};

export type TouchDialogHeaderProps = {
  icon: React.ReactNode;
  iconColor: ThemeColor;
  title: string;
  subtitle?: string;
  tabs?: TouchDialogHeaderTab[];
  selectedTab?: string;
  titleClassName?: string;
  onTabChange?: (key: string) => void;
  extraHeaderContent?: React.ReactNode;
  onDone?: () => void;
  doneLabel?: string;
  onDelete?: () => void;
  deleteLabel?: string;
};

const iconColorClasses: Partial<Record<ThemeColor, string>> = {
  neutral: 'bg-neutral/20 text-neutral',
  primary: 'bg-primary/20 text-primary',
  success: 'bg-success/20 text-success',
  failure: 'bg-failure/20 text-failure',
  warning: 'bg-warning/20 text-warning',
  teal: 'bg-teal/20 text-teal',
  rust: 'bg-rust/20 text-rust',
  purple: 'bg-purple/20 text-purple',
  slate: 'bg-slate/20 text-slate',
  olive: 'bg-olive/20 text-olive',
  brown: 'bg-brown/20 text-brown',
};

//
// ⚛️ ⚛️ ⚛️ TOUCH DIALOG HEADER ⚛️ ⚛️ ⚛️
//
export const TouchDialogHeader = ({
  icon,
  iconColor,
  title,
  subtitle,
  tabs,
  selectedTab,
  onTabChange,
  extraHeaderContent,
  onDone,
  doneLabel,
  onDelete,
  deleteLabel,
  titleClassName,
}: TouchDialogHeaderProps) => (
  <Row
    xBetween
    yCenter
    gap5
    className={cn(
      '@TouchDialogHeader',
      'm-0 h-full w-full',
      tabs && 'relative',
    )}
  >
    <Row gap3>
      <div className="h-[50px] rounded-md bg-white p-1">
        <Row yStretch gap2 className="h-full">
          <MaterialSymbol
            symbolIcon={icon}
            className={cn('h-full w-[40px]', iconColorClasses[iconColor])}
          />
          <Col yCenter className="w-full pb-0.5">
            {subtitle && (
              <span className="text-xs text-neutral/40">{subtitle}</span>
            )}
            <div
              className={cn('text-sm font-medium text-neutral', titleClassName)}
            >
              {title}
            </div>
          </Col>
        </Row>
      </div>
      {extraHeaderContent}
    </Row>

    {tabs && (
      <Row
        xCenter
        yCenter
        gap1
        className="pointer-events-none absolute inset-0"
      >
        <Row xCenter yCenter gap1 className="pointer-events-auto">
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              selected={selectedTab === tab.key}
              onClick={() => onTabChange?.(tab.key)}
              variant="primary"
              className={cn(
                'min-w-[82px] rounded-md border-none px-4 py-4.5 text-center text-base',
                selectedTab === tab.key
                  ? '!border-none !bg-primary/20 !text-primary'
                  : 'text-neutral hover:!bg-neutral/20',
              )}
            >
              {tab.label}
            </Tab>
          ))}
        </Row>
      </Row>
    )}

    <Row gap2 className="[&>*]:min-w-23">
      {onDelete && (
        <TouchButton
          isSelected={false}
          color="failure"
          variant="outline"
          className="h-12 min-h-12"
          onPress={onDelete}
          aria-label={deleteLabel}
        >
          <LucideTrash2 className="size-5" />
        </TouchButton>
      )}
      {onDone && (
        <TouchButton
          isSelected={false}
          color="primary"
          className="h-12 min-h-12 min-w-[100px]"
          onPress={onDone}
          aria-label={doneLabel}
        >
          {doneLabel}
        </TouchButton>
      )}
    </Row>
  </Row>
);
